export enum MONEY_FORMAT_TYPE {
  LITTLE = 'LITTLE',
  MUCH = 'MUCH',
  LOAN = 'LOAN',
  RANGE = 'RANGE',
  TAX = 'TAX',
  MOBILE = 'MOBILE',
  MOBILE2 = 'MOBILE2',
}

export const formatMoney = (value: number, type: MONEY_FORMAT_TYPE, decimals?: number): string => {
  return moneyFormatter(value, type, true, decimals)
}

export const formatMoneyBasedOnWon = (value: string | number, type: MONEY_FORMAT_TYPE, decimals?: number): string => {
  let valueDouble: number = value as number
  if (typeof value === 'string') {
    valueDouble = parseInt(value, 10)
  }
  return moneyFormatter(valueDouble, type, false, decimals)
}
/**
 * money fomatter - 직접 사용 금지
 * ( 만원단위 금액은 formatMoney 원단위 금액은 formatMoneyBasedOnWon 사용 )
 * param value : 금액
 * param type : MUCH / LITTLE / LOAN / TAX / MOBILE / MOBILE2
 * param decimals : 소수점 몇 자리 까지 표현할 것인가 . (옵션)
 * param moneyBasedOnMan : 만원이 기본인 금액인가
 *
 * type - MUCH
 * 1억 이상
 *  - 예. 1023456789  =>  [decimals = 0]  10억 / [decimals = 1]  10.2억 / [decimals = 2] 10,23억
 *      [decimals 없는 경우] 1023456789 => 10.23456789억 / 1023450000 => 10.2345억 / 1000000000 => 10억
 * 1억 미만, 1천 이상
 *  - 예. 12345678  =>  [decimals = 0]  1천 / [decimals = 1]  1.2천 / [decimals = 2] 1,23천
 *      [decimals 없는 경우] 12345678 => 1.2345678천 / 12340000 => 1.234천 / 10000000 => 1천
 * 1천 미만, 1백 이상
 *  - 예. 1234567  =>  [decimals = 0]  1백 / [decimals = 1]  1.2백 / [decimals = 2] 1,23백
 *      [decimals 없는 경우] 1234567 => 1.234567백 / 1230000 => 1.23백 / 1000000 => 1백
 * 1백 미만, 만원 이상
 *  - 예. 123456  =>  [decimals = 0]  12만 / [decimals = 1]  12.3만 / [decimals = 2] 12.34만
 *  -     [decimals 없는 경우] 123456 => 12.3456만 / 123000 => 12.3만 / 120000 => 12만
 * 만원 미만, 1원 이상 (
 * 0원 '' 으로 return
 *
 * type - LITTLE ( 만까지만 표시 )
 * 억 / 만 금액이 있을 경우 '억'단위만 표시
 * (예. 1023456789 -> 10억2,345 / 1000000000 -> 10억 )
 * 만원 미만 '0' 으로 return
 *
 * type - LOAN
 * 억 / 만 / 원 금액이 있을 경우 단위 표시
 * (예. 1023456789 -> 10억2345만6789원 / 1023450000 -> 10억2345만 / 1000000000 -> 10억)
 * 0원 '0' 으로 return
 *
 * type - TAX
 * 억 / 만 / 원 금액이 있을 경우 단위 표시 & 각 단위 앞 띄워쓰기 & 마지막 '원' 무조건 표기
 * (예. 100000000 -> 1억원, 150000000 -> 1억 5,000만원, 150002000 -> 1억 5,000만 2,000원, 15000 -> 1만 5,000원, 5000 -> 5,000원)
 * 0원 '-원' 으로 return
 *
 * type - MOBILE
 * 억 / 만 금액이 있을 경우 '억' 단위만 표시
 * LITTLE 과는 다르게 '억' 뒤에 스페이스 존재
 * (예. 1023456789 -> 10억 2,345 / 1000000000 -> 10억 )
 * 만원 미만 '0' 으로 return
 *
 * type - MOBILE2
 * 억 / 만 금액이 있을 경우 '억' 과 '만' 단위 표시
 * LITTLE 과는 다르게 '억' 뒤에 스페이스 존재
 * (예. 1023456789 -> 10억 2,345만 / 1000000000 -> 10억 )
 * 만원 미만 '0' 으로 return
 */

const moneyFormatter = (value: number, type: MONEY_FORMAT_TYPE, moneyBasedOnMan: boolean, decimals?: number): string => {
  const tenThousand = 10000
  const million = 1000000
  const tenMillion = 10000000
  const hundredMillion = 100000000

  if (typeof value !== 'number') {
    return '0'
  }

  let money = value
  let moneyStr = ''

  let isMinus = false
  if (money < 0) {
    money = money * -1
    isMinus = true
  }

  if (moneyBasedOnMan) {
    money = money * tenThousand
  }

  const overHundredMillion = Math.floor(money / hundredMillion)
  const underHundredMillion = Math.floor(money % hundredMillion)
  const overTenThousand = Math.floor(underHundredMillion / tenThousand)
  const underTenThousand = Math.floor(underHundredMillion % tenThousand)

  switch (type) {
    case MONEY_FORMAT_TYPE.MUCH:
      if (money === 0) {
        break
      }

      let moneyResult = 0
      if (money >= hundredMillion) {
        moneyResult = money / hundredMillion
        moneyStr = '억'
      } else if (money >= tenMillion) {
        moneyResult = money / tenMillion
        moneyStr = '천'
      } else if (money >= million) {
        moneyResult = money / million
        moneyStr = '백'
      } else if (money >= tenThousand) {
        moneyResult = money / tenThousand
        moneyStr = '만'
      } else if (money >= 1) {
        moneyResult = money
        moneyStr = '원'
      }
      if (typeof decimals === 'number') {
        const correct = Math.pow(10, decimals)
        moneyResult = Math.floor(moneyResult * correct) / correct
      }
      moneyStr = moneyResult + moneyStr
      break
    case MONEY_FORMAT_TYPE.LITTLE:
      if (money === 0) {
        moneyStr = '0'
        break
      }

      if (overHundredMillion > 0) {
        moneyStr = overHundredMillion.toLocaleString() + '억'
      }
      if (overTenThousand > 0) {
        moneyStr += overTenThousand.toLocaleString()
      }
      break
    case MONEY_FORMAT_TYPE.LOAN:
      if (money === 0) {
        moneyStr = '0'
        break
      }

      if (overHundredMillion > 0) {
        moneyStr = overHundredMillion.toLocaleString() + '억'
      }
      if (overTenThousand > 0) {
        moneyStr += overTenThousand.toLocaleString() + '만'
      }
      if (underTenThousand > 0) {
        moneyStr += underTenThousand.toLocaleString() + '원'
      }
      break
    case MONEY_FORMAT_TYPE.TAX:
      if (money === 0) {
        moneyStr = '-원'
        break
      }
      if (overHundredMillion > 0) {
        moneyStr = overHundredMillion.toLocaleString() + '억'
      }
      if (overTenThousand > 0) {
        moneyStr += ' ' + overTenThousand.toLocaleString() + '만'
      }
      if (underTenThousand > 0) {
        moneyStr += ' ' + underTenThousand.toLocaleString()
      }
      moneyStr += '원'
      moneyStr.trim()
      break
    case MONEY_FORMAT_TYPE.MOBILE:
      if (money === 0) {
        moneyStr = '0'
        break
      }

      if (overHundredMillion > 0) {
        moneyStr = overHundredMillion.toLocaleString() + '억'
      }

      if (overTenThousand > 0) {
        moneyStr += moneyStr ? ' ' + overTenThousand.toLocaleString() : overTenThousand.toLocaleString()
      }
      break
    case MONEY_FORMAT_TYPE.MOBILE2:
      if (money === 0) {
        moneyStr = '0'
        break
      }

      if (overHundredMillion > 0) {
        moneyStr = overHundredMillion.toLocaleString() + '억'
      }

      if (overTenThousand > 0) {
        moneyStr += moneyStr ? ' ' + overTenThousand.toLocaleString() + '만' : overTenThousand.toLocaleString() + '만'
      }

      break
  }
  if (isMinus) {
    moneyStr = '-' + moneyStr
  }
  return moneyStr
}
